// Settle dates for tickets (ADR 0009 §4.2).
//
// The server has the last word: it stores the settle date it computes on the instrument's
// calendars. A ticket shows the same date by asking the server, and uses the browser's copy of
// the calendars only for the moment before the answer arrives, or when the server cannot be
// reached. It never counts on the book's own calendar, which is the NYSE's: bond, loan and
// currency markets close on days the book is open (Columbus Day, Veterans Day).

import axios from 'axios';
import { calendarsFor } from './businessDate';
import { calculateSettlementDate } from './dateUtils';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export interface SettlementQuery {
  tradeDate: string;
  /** Server ticket type: PUBLIC_FI, LEVERAGED_LOAN, FX, REPO, FUTURES, OPTIONS, IRS, ... */
  ticketType: string;
  /** T+n, e.g. 'T+1'. */
  convention: string;
  currency?: string | null;
  counterCurrency?: string | null;
  /** EQUITY or ETF on a PUBLIC_FI ticket selects the NYSE calendar. */
  securityType?: string | null;
}

/** The calendars the settle date is counted on, as the server resolves them. */
export const settlementCalendars = (q: Omit<SettlementQuery, 'tradeDate' | 'convention'>): string[] =>
  calendarsFor(q.ticketType, q.currency, q.counterCurrency, q.securityType);

/** Counted in the browser on the instrument's calendars. Shown until the server answers. */
export const provisionalSettleDate = (q: SettlementQuery): string =>
  calculateSettlementDate(q.tradeDate, q.convention, settlementCalendars(q));

/** The settle date the server will store; falls back to the provisional date if it cannot answer. */
export const resolveSettleDate = async (q: SettlementQuery): Promise<string> => {
  if (!q.tradeDate) return '';
  try {
    const { data } = await axios.get(`${API_URL}/calendars/settlement`, {
      params: {
        tradeDate: q.tradeDate,
        ticketType: q.ticketType,
        convention: q.convention,
        currency: q.currency || undefined,
        counterCurrency: q.counterCurrency || undefined,
        securityType: q.securityType || undefined,
      },
    });
    return data?.settleDate || provisionalSettleDate(q);
  } catch {
    return provisionalSettleDate(q);
  }
};

/**
 * After a save: the settle date the server stored, and a sentence for the person when it is not
 * the one the ticket sent (a holiday rolled forward). Null when nothing changed.
 */
export const settleDateAdjustment = (sent: string | null | undefined, stored: string | null | undefined): string | null => {
  if (!sent || !stored || sent === stored) return null;
  const [y, m, d] = stored.split('-').map(Number);
  const label = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  return `Settle date moved to ${label}: ${sent} is not a settlement day for this instrument.`;
};
